import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CartContext } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { useCartAnimation } from '../../../context/CartAnimationContext';
import apiService from '../../../services/api';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItemCount } = useContext(CartContext);
  const { currentUser, isAuthenticated, logout } = useAuth();
  const { cartIconRef } = useCartAnimation() || {};
  const [cartGooActive, setCartGooActive] = useState(false);
  const [badgePop, setBadgePop] = useState(false);
  const prevCountRef = useRef(cartItemCount);
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [isSubscriptionsMenuOpen, setIsSubscriptionsMenuOpen] = useState(false);
  const [mobileSubview, setMobileSubview] = useState(null); // null | 'products' | 'subscriptions'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const userMenuContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Product categories for the mega menu
  const productCategories = [
    'Accessories', 'Bags & Backpacks', 'Buckets & Sippers', 'Clothing & Apparel',
    'Drinkware', 'Ears', 'Homeware', 'Jewellery',
    'Lanyards', 'Loungefly', 'Ornaments', 'Pins',
    'Plush', 'Stationery & Art', 'Toys & Games',
  ];
  
  // Cart badge pop + gooey absorption when count increases
  useEffect(() => {
    if (cartItemCount > prevCountRef.current) {
      // Badge pop
      setBadgePop(true);
      setTimeout(() => setBadgePop(false), 450);
      // Gooey absorb
      setCartGooActive(true);
      setTimeout(() => setCartGooActive(false), 500);
    }
    prevCountRef.current = cartItemCount;
  }, [cartItemCount]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close menus when changing routes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsSearchOpen(false);
    setIsProductsMenuOpen(false);
    setIsSubscriptionsMenuOpen(false);
    setMobileSubview(null);
    setSearchQuery('');
    setSearchResults([]);
  }, [location]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        const mobileVisible = mobileSearchRef.current &&
          window.getComputedStyle(mobileSearchRef.current).display !== 'none';
        (mobileVisible ? mobileSearchInputRef.current : searchInputRef.current)?.focus();
      }, 300);
    }
  }, [isSearchOpen]);

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = searchContainerRef.current?.contains(e.target);
      const inMobile = mobileSearchRef.current?.contains(e.target);
      if (!inDesktop && !inMobile) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  // Close user menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuContainerRef.current && !userMenuContainerRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Debounced product search
  const searchProducts = useCallback((query) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = await apiService.products.getAll({ search: query, limit: 3 });
        const products = response.data?.data || response.data || [];
        setSearchResults(products.slice(0, 3));
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  // Toggle user menu
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsMobileMenuOpen(false);
    setMobileSubview(null);
  };
  
  // Toggle search slide-out
  const toggleSearch = () => {
    const opening = !isSearchOpen;
    setIsSearchOpen(opening);
    if (!opening) {
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchProducts(value);
  };

  // Toggle products menu
  const toggleProductsMenu = () => {
    setIsProductsMenuOpen(!isProductsMenuOpen);
  };
  
  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setSearchResults([]);
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Handle category click
  const handleCategoryClick = (category) => {
    const path = `/category/${category.toLowerCase().replace(/[&]+/g, '').replace(/\s+/g, '-').replace(/-{2,}/g, '-')}`;
    navigate(path);
    setIsProductsMenuOpen(false);
  };
  
  return (
    <header className={`header-site-header ${isScrolled ? 'header-scrolled' : ''}`}>
      {/* Main Header Bar */}
      <div className="header-main-bar">
        <div className="header-container">
          {/* Logo - Centered */}
          <div className="header-logo-container">
            <Link to="/" className="header-logo-link">
              <img src="/assets/images/Vector-Logo.webp" alt="Disney Personal Shopper" className="header-logo" />
            </Link>
          </div>
          
          {/* Right side actions */}
          <div className="header-actions">
            {/* Search slide-out */}
            <div className="header-search-wrapper" ref={searchContainerRef}>
              <div className={`header-search-slideout ${isSearchOpen ? 'header-active' : ''}`}>
                <form onSubmit={handleSearchSubmit}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search products..."
                    className="header-search-input"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </form>
                {isSearchOpen && (searchResults.length > 0 || isSearching) && (
                  <div className="header-search-results">
                    {isSearching ? (
                      <div className="header-search-loading">Searching...</div>
                    ) : (
                      searchResults.map((product) => (
                        <Link
                          key={product._id || product.id}
                          to={`/product/${product._id || product.id}`}
                          className="header-search-result-item"
                          onClick={() => {
                            setIsSearchOpen(false);
                            setSearchQuery('');
                            setSearchResults([]);
                          }}
                        >
                          <img
                            src={product.mainImage}
                            alt={product.name}
                            className="header-search-result-image"
                          />
                          <div className="header-search-result-info">
                            <span className="header-search-result-name">{product.name}</span>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
              <button
                className="header-search-toggle"
                onClick={toggleSearch}
                aria-label="Search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path fill="none" d="M0 0h24v24H0z"/>
                  <path d="M18.031 16.617l4.283 4.282-1.415 1.415-4.282-4.283A8.96 8.96 0 0 1 11 20c-4.968 0-9-4.032-9-9s4.032-9 9-9 9 4.032 9 9a8.96 8.96 0 0 1-1.969 5.617zm-2.006-.742A6.977 6.977 0 0 0 18 11c0-3.868-3.133-7-7-7-3.868 0-7 3.132-7 7 0 3.867 3.132 7 7 7a6.977 6.977 0 0 0 4.875-1.975l.15-.15z"/>
                </svg>
              </button>
            </div>
            
            {/* Cart link with item count */}
            <div
              ref={cartIconRef}
              className={`cart-icon-goo-wrapper${cartGooActive ? ' cart-icon-goo-active' : ''}`}
            >
              <Link to="/cart" className="header-cart-link" aria-label="Shopping Cart">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path fill="none" d="M0 0h24v24H0z"/>
                  <path d="M4 6.414L.757 3.172l1.415-1.415L5.414 5h15.242a1 1 0 0 1 .958 1.287l-2.4 8a1 1 0 0 1-.958.713H6v2h11v2H5a1 1 0 0 1-1-1V6.414zM6 7v6h11.512l1.8-6H6zm-.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm12 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
                </svg>
                {cartItemCount > 0 && (
                  <span className={`header-cart-count${badgePop ? ' cart-badge-pop' : ''}`}>
                    {cartItemCount}
                  </span>
                )}
              </Link>
            </div>

            {/* User menu toggle */}
            <div className="header-user-menu-container" ref={userMenuContainerRef}>
              <button
                className="header-user-menu-toggle"
                onClick={toggleUserMenu}
                aria-label="User Menu"
              >
                {isAuthenticated && currentUser ? (
                  <div className="header-user-avatar">
                    {currentUser.firstName.charAt(0)}{currentUser.lastName.charAt(0)}
                  </div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-4.987-3.744A7.966 7.966 0 0 0 12 20c1.97 0 3.773-.712 5.167-1.892A6.979 6.979 0 0 0 12.16 16a6.981 6.981 0 0 0-5.147 2.256zM5.616 16.82A8.975 8.975 0 0 1 12.16 14a8.972 8.972 0 0 1 6.362 2.634 8 8 0 1 0-12.906.187zM12 13a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                  </svg>
                )}
              </button>
              
              {isUserMenuOpen && (
                <div className="header-user-dropdown-menu">
                  {isAuthenticated && currentUser ? (
                    <>
                      <div className="header-user-dropdown-header">
                        <p className="header-user-dropdown-name">{currentUser.firstName} {currentUser.lastName}</p>
                        <p className="header-user-dropdown-email">{currentUser.email}</p>
                      </div>
                      <Link to="/profile" className="header-user-dropdown-item">My Profile</Link>
                      <Link to="/my-requests" className="header-user-dropdown-item">My Requests</Link>
                      <Link to="/orders" className="header-user-dropdown-item">Order History</Link>
                      {currentUser.role === 'admin' && (
                        <>
                          <div className="header-dropdown-divider"></div>
                          <div className="header-dropdown-section-title">Admin</div>
                          <Link to="/admin/requests" className="header-user-dropdown-item">Request Dashboard</Link>
                          <Link to="/admin/orders" className="header-user-dropdown-item">Orders</Link>
                          <Link to="/admin/collect" className="header-user-dropdown-item">Collect List</Link>
                          <Link to="/admin/inventory" className="header-user-dropdown-item">Inventory Tracking</Link>
                          <Link to="/admin/products" className='header-user-dropdown-item'>Product Management</Link>
                          <Link to="/admin/featured-loot" className="header-user-dropdown-item">Featured Loot</Link>
                          <Link to="/admin/subscriptions" className="header-user-dropdown-item">Subscriptions</Link>
                          <Link to="/admin/users" className="header-user-dropdown-item">User Management</Link>
                          <Link to="/admin/settings" className="header-user-dropdown-item">Settings</Link>
                        </>
                      )}
                      <div className="header-dropdown-divider"></div>
                      <button onClick={handleLogout} className="header-user-dropdown-item header-logout-button">Logout</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="header-user-dropdown-item">Login</Link>
                      <Link to="/register" className="header-user-dropdown-item">Register</Link>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Mobile menu toggle */}
            <button 
              className="header-mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
            >
              <span className={`header-hamburger ${isMobileMenuOpen ? 'header-active' : ''}`}>
                <span className="header-hamburger-line"></span>
                <span className="header-hamburger-line"></span>
                <span className="header-hamburger-line"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Search Dropdown */}
      <div
        className={`header-mobile-search-drop ${isSearchOpen ? 'header-active' : ''}`}
        ref={mobileSearchRef}
      >
        <div className="header-mobile-search-inner">
          <form onSubmit={handleSearchSubmit}>
            <input
              ref={mobileSearchInputRef}
              type="text"
              placeholder="Search products..."
              className="header-search-input"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </form>
          {(searchResults.length > 0 || isSearching) && (
            <div className="header-mobile-search-results">
              {isSearching ? (
                <div className="header-search-loading">Searching...</div>
              ) : (
                searchResults.map((product) => (
                  <Link
                    key={product._id || product.id}
                    to={`/product/${product._id || product.id}`}
                    className="header-search-result-item"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <img src={product.mainImage} alt={product.name} className="header-search-result-image" />
                    <div className="header-search-result-info">
                      <span className="header-search-result-name">{product.name}</span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Secondary Navigation Bar */}
      <div className="header-secondary-bar">
        <nav className="header-main-nav header-desktop-nav">
          <ul className="header-nav-list">
            <li className="header-nav-item">
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'header-active' : ''}
              >
                HOME
              </Link>
            </li>
            
            {/* Products menu */}
            <li 
              className={`header-nav-item header-products-dropdown ${isProductsMenuOpen ? 'header-open' : ''}`}
              onMouseEnter={() => setIsProductsMenuOpen(true)}
              onMouseLeave={() => setIsProductsMenuOpen(false)}
            >
              <span 
                className={`header-nav-link ${location.pathname.includes('/category') ? 'header-active' : ''}`}
              >
                <Link to="/category/all">
                  PRODUCTS
                </Link>
              </span>
            </li>
            
            <li className="header-nav-item">
              <Link 
                to="/request-item" 
                className={location.pathname === '/request-item' ? 'header-active' : ''}
              >
                MAKE A REQUEST
              </Link>
            </li>
            
            {/* Subscriptions menu */}
            <li 
              className={`header-nav-item header-subscriptions-dropdown ${isSubscriptionsMenuOpen ? 'header-open' : ''}`}
              onMouseEnter={() => setIsSubscriptionsMenuOpen(true)}
              onMouseLeave={() => setIsSubscriptionsMenuOpen(false)}
            >
              <span 
                className={`header-nav-link ${location.pathname.includes('/subscription') ? 'header-active' : ''}`}
              >
                SUBSCRIPTIONS
              </span>
              <div className="header-subscriptions-dropdown-menu">
                {/* <Link to="/subscriptions/monthly-pins" className="header-dropdown-item">
                  MONTHLY PINS
                </Link> */}
                <Link to="/subscriptions/mystery-pins" className="header-dropdown-item">
                  MYSTERY PINS
                </Link>
              </div>
            </li>
            
            <li className="header-nav-item">
              <Link
                to="/category/all?onSale=true"
                className={location.search === '?onSale=true' ? 'header-active' : ''}
              >
                SALE
              </Link>
            </li>

            <li className="header-nav-item">
              <Link
                to="/about"
                className={location.pathname === '/about' ? 'header-active' : ''}
              >
                ABOUT US
              </Link>
            </li>
            
            <li className="header-nav-item">
              <Link
                to="/vault"
                className={location.pathname === '/vault' ? 'header-active' : ''}
              >
                VAULT
              </Link>
            </li>
          </ul>
        </nav>
      </div>
      
      {/* Products Mega Menu */}
      <div 
        className={`header-products-mega-menu ${isProductsMenuOpen ? 'header-open' : ''}`}
        onMouseEnter={() => setIsProductsMenuOpen(true)}
        onMouseLeave={() => setIsProductsMenuOpen(false)}
      >
        <div className="header-products-grid">
          {productCategories.map((category, index) => (
            <button
              key={index}
              className="header-product-category-item"
              onClick={() => handleCategoryClick(category)}
            >
              {category.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <nav className={`header-mobile-nav ${isMobileMenuOpen ? 'header-open' : ''}`}>
        <div className="hmenu-views-wrapper">

          {/* Main view */}
          <div className={`hmenu-view hmenu-main-view${mobileSubview ? ' hmenu-main-view--hidden' : ''}`}>
            <ul className="hmenu-list">
              <li className="hmenu-item">
                <Link to="/">HOME</Link>
              </li>
              <li className="hmenu-item">
                <Link to={isAuthenticated && currentUser ? '/profile' : '/login'}>ACCOUNT</Link>
              </li>
              <li className="hmenu-item">
                <button onClick={() => setMobileSubview('products')}>
                  PRODUCTS
                  <svg className="hmenu-arrow-right" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                  </svg>
                </button>
              </li>
              <li className="hmenu-item">
                <Link to="/request-item">MAKE A REQUEST</Link>
              </li>
              <li className="hmenu-item">
                <Link to="/about">ABOUT US</Link>
              </li>
              <li className="hmenu-item">
                <button onClick={() => setMobileSubview('subscriptions')}>
                  SUBSCRIPTIONS
                  <svg className="hmenu-arrow-right" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                  </svg>
                </button>
              </li>
              <li className="hmenu-item">
                <Link to="/category/all?onSale=true">SALE</Link>
              </li>
              <li className="hmenu-item">
                <Link to="/vault">VAULT</Link>
              </li>
              <li className="hmenu-item">
                <Link to="/contact">CONTACT</Link>
              </li>
              {isAuthenticated && currentUser && (
                <li className="hmenu-item">
                  <button className="hmenu-logout" onClick={handleLogout}>LOGOUT</button>
                </li>
              )}
            </ul>
          </div>

          {/* Products subview */}
          <div className={`hmenu-view hmenu-subview${mobileSubview === 'products' ? ' hmenu-subview--active' : ''}`}>
            <button className="hmenu-back-btn" onClick={() => setMobileSubview(null)}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
            </button>
            <ul className="hmenu-list">
              {productCategories.map((category, index) => (
                <li key={index} className="hmenu-item">
                  <button onClick={() => handleCategoryClick(category)}>
                    {category.toUpperCase()}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Subscriptions subview */}
          <div className={`hmenu-view hmenu-subview${mobileSubview === 'subscriptions' ? ' hmenu-subview--active' : ''}`}>
            <button className="hmenu-back-btn" onClick={() => setMobileSubview(null)}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
            </button>
            <ul className="hmenu-list">
              <li className="hmenu-item">
                <Link to="/subscriptions/mystery-pins">MYSTERY PINS</Link>
              </li>
            </ul>
          </div>

        </div>
      </nav>
      


    </header>
  );
};

export default Header;